import React, { Component } from 'react'
import upperFirst from 'lodash/upperFirst'
import classNames from 'classnames'
import { withTranslation } from 'react-i18next'
import validateImage from 'app/components/Inputs/validateImage'
import ErrorLabel from 'app/components/ErrorLabel/ErrorLabel'
import ImageCropper from 'app/components/ImageCropper'
import isUrl from 'is-url'

type Props = {
  entityName: string,
  extensions: string[],
  mimes: string[],
  minSize: number,
  maxSize: number,
  title: string,
  cropper: any,
  wrapperClassName: string,
  meta: {
    error: ?string | ?(string[]),
    touched: boolean,
  },
  input: {
    onChange: (value: string) => void,
    onBlur: () => void,
    value: ?string,
    name: string,
  },
  uploadImageRequest: any,
  renderColorPickerField: void,
  color: ?string,
  // isSkipCropper if we want download initial and custom  size without cropper
  isSkipCropper: ?boolean,
  changeReduxForm: () => void,
}

type State = {
  isImageModalOpen: boolean,
  file: ?File,
  mime: ?string,
  ext: ?string,
  prevImage: ?string,
}

class ImageInput extends Component<Props, State> {
  static defaultProps = {
    entityName: 'image',
    extensions: ['jpg', 'png', 'jpeg'],
    mimes: ['image/jpeg', 'image/png'],
    title: '',
    wrapperClassName: '',
    color: '#f5f7fa',
  }

  state = {
    isImageModalOpen: false,
    file: null,
    mime: null,
    ext: null,
    prevImage: null,
  }

  id: string
  onFileInputChange: (event: SyntheticEvent<HTMLInputElement>) => void
  onRemove: () => void

  constructor(props: Props, ...opts: any[]) {
    super(props, ...opts)
    this.id = `__image-input-${Math.random()
      .toString()
      .substr(2, 10)}`
    this.onFileInputChange = this.onFileInputChange.bind(this)
    this.onRemove = this.onRemove.bind(this)
  }

  onFileInputChange = async (event: SyntheticEvent<HTMLInputElement>) => {
    if (event.target.files instanceof FileList && event.target.files.length && event.target.files[0] instanceof File) {
      const file = event.target.files[0]
      const error = await validateImage({
        mimes: this.props.mimes,
        minSize: this.props.minSize,
        maxSize: this.props.maxSize,
        file,
      })
      if (error) {
        this.props.input.onChange(error)
        this.props.input.onBlur()
      } else {
        const mime = file.type
        const ext = file.name.substring(file.name.lastIndexOf('.') + 1)
        const originFileName = file.name
        if (this.props.cropper != null && !this.props.isSkipCropper) {
          this.setState({
            file,
            mime,
            ext,
          })
          this.props.showModal(
            <ImageCropper
              {...this.props.cropper}
              isOpen={this.state.isImageModalOpen}
              file={this.state.file}
              title={`Position and size ${upperFirst(this.props.entityName)}`}
              organizations={this.props.organizations}
              handleOnChange={this.props.handleOnChange}
              onCancel={() => {
                this.setState({ isImageModalOpen: false, file: null })
              }}
              isModal
              onSave={(
                canvas: HTMLCanvasElement,
                canvasScaled: HTMLCanvasElement,
                scale: number,
                isConfirmRewriteImage: boolean
              ) => {
                if (this.state.mime != null && this.state.ext != null) {
                  const mime = scale < 100 ? 'image/png' : this.state.mime
                  canvasScaled.name = originFileName
                  this.props.uploadImageRequest(
                    canvasScaled,
                    mime,
                    this.state.ext,
                    this.props.input.name,
                    isConfirmRewriteImage
                  )
                }
                this.setState({ isImageModalOpen: false, file: null, mime: null, ext: null })
              }}
            />,
            { size: 'sm' }
          )
        } else {
          // create field with width for image, it needs in emails
          const _URL = window.URL || window.webkitURL
          const image = new Image()
          const change = function() {
            this.props.changeReduxForm(this.props.meta.form, `${this.props.input.name}Width`, image.width)
          }
          image.onload = change.bind(this)
          image.src = _URL.createObjectURL(file)

          this.props.uploadImageRequest(file, mime, ext, this.props.input.name)
        }
      }
    }
  }

  onRemove = () => {
    this.props.input.onChange('')
  }

  renderLabel() {
    let string = ''
    const { t, minSize, maxSize, extensions, entityName } = this.props
    if (maxSize && minSize && minSize.width === maxSize.width && minSize.height === maxSize.height) {
      string += t('Size of', { width: minSize.width, height: minSize.height, entityName })
    } else if (minSize || maxSize) {
      if (minSize) {
        // TODO: @Serginyo90 remove from locise old version when this code will be deployed to prod
        string += t('Minimum size of', { width: minSize.width, height: minSize.height, entityName })
      }
      if (maxSize) {
        string += t('Maximum size of', { width: maxSize.width, height: maxSize.height, entityName })
      }
    }
    string += `${t('File format')} ${extensions.map(extension => `.${extension}`).join(', ')}.`
    return (
      <label htmlFor="nothing" className="image-input__format-label">
        {string}
      </label>
    )
  }

  render() {
    const {
      t,
      meta: { touched, error },
      entityName,
      wrapperClassName,
      title,
      mimes,
      input,
      renderColorPickerField,
      color,
    } = this.props
    const uploadBtnText = isUrl(input.value) ? t('Replace') : t(`Upload ${upperFirst(entityName)}`)
    return (
      <div className={`image-input ${wrapperClassName}`}>
        {title ? <div className="image-input__title">{t(title)}</div> : null}
        <div className="image-input__row">
          <div className="image-input__img-holder" style={{ backgroundColor: color }}>
            {isUrl(input.value) ? (
              <img className="image-input__img" src={input.value} alt={input.value} />
            ) : (
              <div className="image-input__no-image-text">{t(`NO ${entityName.toUpperCase()}`)}</div>
            )}
          </div>
          <div className="image-input__controls">
            <div className="image-input__btn-group">
              <label
                className={classNames('btn btn--xs', {
                  'btn--outline': !isUrl(input.value),
                  'btn--default': isUrl(input.value),
                })}
                htmlFor={this.id}
              >
                {uploadBtnText}
                <input
                  type="file"
                  style={{ display: 'none' }}
                  accept={mimes.join(',')}
                  id={this.id}
                  onChange={event => {
                    this.onFileInputChange(event)
                    event.target.value = null
                  }}
                />
              </label>
              {isUrl(input.value) && (
                <button className="btn btn--xs btn--default" onClick={this.onRemove}>
                  {t('Remove')}
                </button>
              )}
            </div>
            <div className="image-input__labels">
              <label htmlFor="nothing" className="image-input__errors">
                {touched &&
                  error && <ErrorLabel before={`${t(upperFirst(entityName))}:`} htmlFor="nothing" error={t(error)} />}
              </label>
              {this.renderLabel()}
              {renderColorPickerField && renderColorPickerField()}
            </div>
          </div>
        </div>
      </div>
    )
  }
}

ImageInput.displayName = 'ImageInput'

export default withTranslation()(ImageInput)
